<template>
  <div class="material-detail-page">
    <div v-if="loading" class="loading">Loading material…</div>

    <div v-else-if="error" class="error">
      <p>Failed to load material.</p>
      <pre>{{ error }}</pre>
    </div>

    <div v-else-if="material" class="details-grid">
      <EntityOverviewLayout :panelTabs="overviewPanels" defaultPanel="files">
        <template #top-left>
          <MaterialDetails
            :material="material"
            :showMetadata="false"
            @updateMaterial="handleUpdateMaterial"
          />
        </template>

        <template #top-right>
          <DisplayImageGallery entityType="material" :entityId="material.id" />
        </template>

        <template #bottom-left>
          <NodeMdList
            :key="material.node_id"
            :mdListId="material.metadata_list_id"
            :assType="'material'"
            :assId="material.node_id"
            @mdListCreated="handleMdListCreated"
            @mdListDetached="handleMdListDetached"
          />
        </template>

        <template #panel-files>
          <ExperimentFiles
            ref="filesComp"
            v-if="material && files"
            :files="files"
            :folders="folders"
            :base-path="filesBasePath"
            :loadingTree="isFilesTreeLoading"
            :experimentId="material.id"
            :assId="material.id"
            :assType="'material'"
            :isUploading="isUploading"
            :uploadPercent="uploadPercent"
            :uploadLabel="uploadLabel"
            @addFiles="addFiles"
            @removeFile="removeFile"
          />
        </template>

        <template #panel-comments>
          <ExperimentComments
            :comments="comments"
            :users="users"
            :files="files"
            :entityId="material.id"
            @addComment="addComment"
            @removeComment="removeComment"
            @updateComment="handleUpdateComment"
          />
        </template>
      </EntityOverviewLayout>
    </div>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />

    <div v-if="!loading && !error && !material" class="empty">Material not found.</div>
  </div>
</template>

<script>
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import MaterialDetails from '@/components/MaterialDetails.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import NodeMdList from '@/components/NodeMdList.vue';
import FileUploadSession from '@/components/FileUploadSession.vue';
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from 'axios';
import { parseFilesTree } from '@/utils/filesTree';

export default {
  name: 'MaterialDetailPage',
  components: { 
    MaterialDetails,
    ExperimentFiles,
    ExperimentComments,
    NodeMdList,
    FileUploadSession,
    EntityOverviewLayout,
    DisplayImageGallery,
  },
  data() {
    return {
      material: null,
      loading: false,
      error: null,
      files: [],
      folders: [],
      comments: [],
      users: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    materialId() {
      return Number(this.$route.params.id);
    },
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
      ];
    },
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  created() {
    this.fetchMaterial();
    this.fetchComments();
    this.fetchUsers();
  },
  watch: {
    // Refetch if route id changes
    '$route.params.id'() {
      this.fetchMaterial();
      this.fetchComments();
    },
  },
  methods: {
    async handleMdListCreated(newMdListId) {
      await this.handleUpdateMaterial({
        ...this.material,
        metadata_list_id: newMdListId?.id ?? this.material?.metadata_list_id,
      });
    },
    async handleMdListDetached() {
      await this.handleUpdateMaterial({
        ...this.material,
        metadata_list_id: null,
      });
    },
    async fetchMaterial() {
      this.loading = true;
      this.error = null;
      try {
        this.material = await apiService.fetchResources(
            API_ENDPOINTS.material(this.materialId),
            this.loggedInUser.entra_id
        );
        this.fetchFiles()
      } catch (e) {
        this.error = e?.message || String(e);
        this.material = null;
      } finally {
        this.loading = false;
      }
    },
    async fetchFiles() {
      const materialId = this.material.id;
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'material',
            entity_id: materialId,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        const { files, folders, basePath } = parseFilesTree(response.data);
        this.files = files;
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        console.error('Error fetching files/folders:', error);
        await this.fetchFilesLegacy();
      } finally {
        this.isFilesTreeLoading = false;
      }
    },
    async fetchFilesLegacy() {
      const materialId = this.material.id;
      try {
        const [filesRes, foldersRes] = await Promise.all([
          axios.get(API_ENDPOINTS.files, { params: { file_type: 'material', entity_id: materialId, entra_id: this.loggedInUser.entra_id } }),
          axios.get(API_ENDPOINTS.folders, { params: { associative_type: 'material', associative_id: materialId, entra_id: this.loggedInUser.entra_id } }),
        ]);
        this.files = filesRes.data || [];
        this.folders = (foldersRes.data || []).map((folder) => ({
          ...folder,
          id: folder?.id ?? `virtual:${folder?.internal_path || folder?.path || Math.random().toString(36).slice(2)}`,
          backend_id: folder?.id ?? null,
          internal_path: folder?.internal_path || folder?.path || "",
          parent_id: folder?.parent_id ?? null,
          virtual: Boolean(folder?.virtual),
          entity_type: folder?.entity_type || null,
          entity_id: folder?.entity_id ?? null,
        }));
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.toast.error('Failed to fetch files');
          console.error('Failed to fetch files (legacy):', error);
        }
      }
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'material',
          entityId: this.materialId,
          entraId: this.loggedInUser?.entra_id,
        });
      } catch (error) {
        if (error?.response?.status === 404) {
          this.comments = [];
          return;
        }
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch material comments:', error);
      }
    },
    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser?.entra_id,
        });
      } catch (error) {
        this.users = [];
        console.error('Failed to fetch users:', error);
      }
    },
    async addFiles({ files, id, folderId }) {
      this.uploadSession = {
        visible: true,
        fileType: 'material',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },

    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },

    handleUploadError(error) {
      console.error('Upload session error:', error);
    },
    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success('File removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },
    async addComment(commentText, author_id) {
      try {
        await this.discussion.addComment({
          entityType: 'material',
          entityId: this.materialId,
          text: commentText,
          authorId: author_id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        this.toast.success('Comment added successfully!');
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add material comment:', error);
      }
    },
    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser?.id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        this.toast.success('Comment removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove material comment:', error);
      }
    },
    async handleUpdateComment(commentId, newText, materialId) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'material',
          entityId: materialId ?? this.materialId,
          text: newText,
          userId: this.loggedInUser?.id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        this.toast.success('Comment updated successfully!');
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update material comment:', error);
      }
    },
    async handleUpdateMaterial(updated) {
      try {
        // Only send fields your backend expects
        const payload = {
          name: updated.name,
          batch: updated.batch,
          metadata_list_id: updated.metadata_list_id,
        };

        const res = await apiService.updateResource(
            API_ENDPOINTS.material(updated.id),
            payload,
            this.loggedInUser.entra_id
        );

        // Prefer the server response; fallback to merging payload
        this.material = res || { ...this.material, ...payload };
        this.toast.success('Material updated');
      } catch (e) {
        this.toast.error('Failed to update material');
        // Keep current data; log for debugging
        console.error('Update material error:', e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.material-detail-page {
  max-width: 1480px;
  margin: 20px auto;
  padding: 0 10px;

  .loading,
  .error,
  .empty {
    background: $secondary-color;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  }

  .error pre {
    white-space: pre-wrap;
    margin-top: 8px;
    font-size: 0.9rem;
    color: #b00020;
  }
}
</style>
